import { AxiosError, AxiosResponse } from "axios";
import { api } from "../http";
import { store } from "../store";
import { DefaultRes } from "../types";
import { RESPONSE_CODES } from "../constants/responseCodes.constants";
import { toast } from "react-toastify";
import { MessageCenterTypes } from "../types/message-center.types";
import { legacySendTimesFor } from "../constants/messageReminders.constants";
import { PaginationFactory, PaginatorFactory } from "../factory";
import queryStringify from "qs-stringify";
import { RecipientCriteria } from "./compose-search.types";

export class MessageCenterService {
    public static readonly dispatch = store.dispatch.messageCenter;

    /**
     * Create a message batch (handles both immediate and scheduled messages)
     */
    public static async createMessage(
        title: string,
        body: string,
        recipientUserIds: string[],
        deliveryChannels: string[],
        scheduleType: 'IMMEDIATE' | 'SCHEDULED',
        isAnnouncement: boolean,
        isCritical: boolean,
        scheduledDate?: Date | null,
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageBatch) => void,
        recipientCriteria?: RecipientCriteria,
    ): Promise<MessageCenterTypes.MessageBatch | void> {

        MessageCenterService.dispatch.toggleLoading('createMessage', true);
        try {
            const requestData: MessageCenterTypes.CreateMessageRequest = {
                title,
                body,
                messageType: scheduleType,
                deliveryDate: scheduleType === 'SCHEDULED' && scheduledDate
                    ? scheduledDate.toISOString()
                    : null,
                channels: deliveryChannels.map(channel => channel.toUpperCase()),
                isAnnouncement,
                isCritical,
            };

            if (recipientCriteria && Object.keys(recipientCriteria).length > 0) {
                requestData.recipientCriteria = recipientCriteria;
            } else {
                requestData.recipientUserIds = recipientUserIds;
            }

            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<MessageCenterTypes.MessageBatch>,
                AxiosResponse<DefaultRes<MessageCenterTypes.MessageBatch>>
            >('/messages', requestData);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;

        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MessageCenterTypes.MessageBatch>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        } finally {
            MessageCenterService.dispatch.toggleLoading('createMessage', false);
        }
    }

    /**
     * Get scheduled messages for the dashboard
     */
    public static async getScheduledMessages(
        page: number = 1,
        perPage: number = 100,
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageBatch[]) => void,
    ): Promise<MessageCenterTypes.MessageBatch[] | void> {

        MessageCenterService.dispatch.toggleLoading('getScheduledMessages', true);
        
        try {
            const dto: PaginatorFactory<{}> = {
                page,
                perPage,
            } // No filter or sort needed

            const result = await MessageCenterService.fetchMany(
                dto,
                undefined,
                true,
                true,
                undefined,
            );

            MessageCenterService.dispatch.toggleLoading('getScheduledMessages', false);

            const scheduledMessages = result?.items || [];

            callback && callback(true, 'SUCCESS', scheduledMessages);
            return scheduledMessages;
        } catch (error: any) {
            MessageCenterService.dispatch.toggleLoading('getScheduledMessages', false);
            
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<{ items: MessageCenterTypes.MessageBatch[] }>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Delete a scheduled message
     */
    public static async deleteScheduledMessage(
        messageId: string,
        callback?: (isValid?: boolean, message?: string) => void,
    ): Promise<void> {
        
        try {
            const {
                data: { isValid, message },
            } = await api.delete<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`/messages/${messageId}`);

            callback && callback(isValid, RESPONSE_CODES[message]);

        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<any>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Get message batch by ID for editing
     */
    public static async getMessageById(
        messageId: string,
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageBatch) => void,
    ): Promise<MessageCenterTypes.MessageBatch | void> {
        
        try {
            const {
                data: { result, isValid, message },
            } = await api.get<
                DefaultRes<MessageCenterTypes.MessageBatch>,
                AxiosResponse<DefaultRes<MessageCenterTypes.MessageBatch>>
            >(`/messages/${messageId}`);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;

        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MessageCenterTypes.MessageBatch>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Update an existing message batch
     */
    public static async updateMessage(
        messageId: string,
        title: string,
        body: string,
        recipientUserIds: string[],
        deliveryChannels: string[],
        scheduleType: 'IMMEDIATE' | 'SCHEDULED',
        isAnnouncement: boolean,
        isCritical: boolean,
        scheduledDate?: Date | null,
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageBatch) => void,
        recipientCriteria?: RecipientCriteria,
    ): Promise<MessageCenterTypes.MessageBatch | void> {

        MessageCenterService.dispatch.toggleLoading('updateMessage', true);

        try {
            const requestData: MessageCenterTypes.CreateMessageRequest = {
                title,
                body,
                messageType: scheduleType,
                deliveryDate: scheduleType === 'SCHEDULED' && scheduledDate
                    ? scheduledDate.toISOString()
                    : null,
                channels: deliveryChannels.map(channel => channel.toUpperCase()),
                isAnnouncement,
                isCritical,
            };

            if (recipientCriteria && Object.keys(recipientCriteria).length > 0) {
                requestData.recipientCriteria = recipientCriteria;
            } else {
                requestData.recipientUserIds = recipientUserIds;
            }

            const {
                data: { result, isValid, message },
            } = await api.put<
                DefaultRes<MessageCenterTypes.MessageBatch>,
                AxiosResponse<DefaultRes<MessageCenterTypes.MessageBatch>>
            >(`/messages/${messageId}`, requestData);

            MessageCenterService.dispatch.toggleLoading('updateMessage', false);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;

        } catch (error: any) {
            MessageCenterService.dispatch.toggleLoading('updateMessage', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MessageCenterTypes.MessageBatch>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Initialize edit mode - fetch message and populate Redux state
     */
    public static async initializeMessageEditMode(
        messageId: string,
        callback?: (isValid?: boolean, message?: string) => void,
    ): Promise<void> {
        
        try {
            // Get the message details
            const message = await MessageCenterService.getMessageById(
                messageId,
                (isValid, errorMessage, result) => {
                    if (!isValid || !result) {
                        callback && callback(false, errorMessage || 'Failed to load message');
                        return;
                    }

                    // Set edit mode in Redux
                    MessageCenterService.dispatch.setEditMode({
                        batchId: messageId,
                        originalBatch: result,
                        reminderId: null,
                        originalReminder: null
                    });

                    MessageCenterService.dispatch.updateSelectedUserIds(result.recipientUserIds);

                    // Populate recipientCriteria for the new V2 component
                    const defaultCriteria: RecipientCriteria = {
                        groupIds: [],
                        teamIds: [],
                        userIds: [],
                        selectAllUsers: false,
                        activeFilter: 'all',
                        tagFilters: [],
                        tagLogic: 'AND',
                    };

                    if (result.recipientCriteria) {
                        MessageCenterService.dispatch.setRecipientCriteria({
                            ...defaultCriteria,
                            ...result.recipientCriteria,
                        });
                    } else {
                        // Legacy message: convert flat userIds to criteria format
                        MessageCenterService.dispatch.setRecipientCriteria({
                            ...defaultCriteria,
                            userIds: result.recipientUserIds,
                        });
                    }

                    // Pre-populate form state
                    MessageCenterService.dispatch.updateFormState({
                        title: result.title,
                        body: result.body,
                        channels: result.channels,
                        messageType: result.messageType === 'IMMEDIATE' ? 'IMMEDIATE' : 'SCHEDULED',
                        scheduledDate: result.deliveryDate,
                        isAnnouncement: result.isAnnouncement,
                        isCritical: result.isCritical,
                    });

                    callback && callback(true, 'Edit mode initialized successfully');
                }
            );

        } catch (error: any) {
            callback && callback(false, String(error));
        }
    }

    /**
     * A backend without scheduling support silently strips unknown fields
     * (no .strict() on the reminder schema), so a save can "succeed" while
     * the schedule is discarded and the reminder stays on the legacy
     * 8am/8pm cycle. Detect that by checking the response echoes the
     * schedule we submitted, and warn the admin instead of failing silently.
     */
    private static warnIfScheduleNotEchoed(
        schedule: { sendTimes: string[]; timezone: string; timezoneStrategy?: 'fixed' | 'perRecipient' } | undefined,
        isValid?: boolean,
        result?: MessageCenterTypes.MessageReminder,
    ): void {
        // Self-contained: this is advisory-only and shares the caller's
        // try/catch, whose catch reports save failure — a throw here must
        // never flip a successful save into a reported failure.
        try {
            if (!schedule || !isValid || !result) return;
            // Order-independent: the admin may pick times in any order and a
            // server-side sort must not read as "schedule was stripped".
            // (A stripped schedule still mismatches: '' vs the joined times.)
            const sorted = (a?: string[] | null) => [...(a ?? [])].sort().join(',');
            const echoed = sorted(result.sendTimes) === sorted(schedule.sendTimes)
                && result.timezone === schedule.timezone;
            if (!echoed) {
                toast.warn(
                    'The send time was not saved — the server does not support scheduling yet. '
                    + 'This reminder will keep its previous schedule.',
                );
            } else if (
                schedule.timezoneStrategy === 'perRecipient'
                && result.timezoneStrategy !== 'perRecipient'
            ) {
                // Same silent-strip hazard, one field newer: an API without
                // Phase 2 echoes the schedule but drops the strategy.
                toast.warn(
                    'Per-recipient local time was not saved — the server does not support it yet. '
                    + 'This reminder will send on the fixed time zone shown.',
                );
            }
        } catch (err) {
            console.error('Schedule echo-check failed (save unaffected):', err);
        }
    }

    /**
     * Create an automated reminder
     */
    public static async createAutomatedReminder(
        title: string,
        body: string,
        recipientUserIds: string[],
        channels: string[],
        reminderType: 'CHARGING' | 'STAY_CONNECTED' | 'UPDATE_REMINDER',
        intervalHours: number,
        daysOfWeek: string[],
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageReminder) => void,
        recipientCriteria?: RecipientCriteria,
        schedule?: { sendTimes: string[]; timezone: string; timezoneStrategy?: 'fixed' | 'perRecipient' },
    ): Promise<MessageCenterTypes.MessageReminder | void> {

        MessageCenterService.dispatch.toggleLoading('createMessage', true);

        try {
            const requestData: MessageCenterTypes.CreateAutomatedReminderRequest = {
                title,
                body,
                channels: channels.map(channel => channel.toUpperCase()),
                reminderType,
                intervalHours,
                daysOfWeek,
                ...(schedule ? { sendTimes: schedule.sendTimes, timezone: schedule.timezone, ...(schedule.timezoneStrategy ? { timezoneStrategy: schedule.timezoneStrategy } : {}) } : {}),
            };

            if (recipientCriteria && Object.keys(recipientCriteria).length > 0) {
                requestData.recipientCriteria = recipientCriteria;
            } else {
                requestData.recipientUserIds = recipientUserIds;
            }

            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<MessageCenterTypes.MessageReminder>,
                AxiosResponse<DefaultRes<MessageCenterTypes.MessageReminder>>
            >('/message-reminders', requestData);

            MessageCenterService.dispatch.toggleLoading('createMessage', false);

            MessageCenterService.warnIfScheduleNotEchoed(schedule, isValid, result);
            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;

        } catch (error: any) {
            MessageCenterService.dispatch.toggleLoading('createMessage', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MessageCenterTypes.MessageReminder>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Get automated reminders (paginated)
     */
    public static async getAutomatedReminders(
        page: number = 1,
        perPage: number = 100,
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageReminder[]) => void,
    ): Promise<MessageCenterTypes.MessageReminder[] | void> {
        
        MessageCenterService.dispatch.toggleLoading('getScheduledMessages', true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.get<
                DefaultRes<{ items: MessageCenterTypes.MessageReminder[], page: number, perPage: number, total: number }>,
                AxiosResponse<DefaultRes<{ items: MessageCenterTypes.MessageReminder[], page: number, perPage: number, total: number }>>
            >(`/message-reminders?page=${page}&perPage=${perPage}`);

            MessageCenterService.dispatch.toggleLoading('getScheduledMessages', false);

            // Extract the items array from the paginated response
            const reminders = result?.items || [];
            
            callback && callback(isValid, RESPONSE_CODES[message], reminders);
            return reminders;

        } catch (error: any) {
            MessageCenterService.dispatch.toggleLoading('getScheduledMessages', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<{ items: MessageCenterTypes.MessageReminder[] }>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Get single automated reminder by ID
     */
    public static async getAutomatedReminderById(
        reminderId: string,
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageReminder) => void,
    ): Promise<MessageCenterTypes.MessageReminder | void> {
        try {
            const {
                data: { result, isValid, message },
            } = await api.get<
                DefaultRes<MessageCenterTypes.MessageReminder>,
                AxiosResponse<DefaultRes<MessageCenterTypes.MessageReminder>>
            >(`/message-reminders/${reminderId}`);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;

        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MessageCenterTypes.MessageReminder>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Update an automated reminder
     */
    public static async updateAutomatedReminder(
        reminderId: string,
        title: string,
        body: string,
        recipientUserIds: string[],
        channels: string[],
        reminderType: 'CHARGING' | 'STAY_CONNECTED' | 'UPDATE_REMINDER',
        intervalHours: number,
        daysOfWeek: string[],
        isActive: boolean,
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageReminder) => void,
        recipientCriteria?: RecipientCriteria,
        schedule?: { sendTimes: string[]; timezone: string; timezoneStrategy?: 'fixed' | 'perRecipient' },
    ): Promise<MessageCenterTypes.MessageReminder | void> {

        MessageCenterService.dispatch.toggleLoading('updateMessage', true);

        try {
            const requestData: MessageCenterTypes.UpdateAutomatedReminderRequest = {
                title,
                body,
                channels: channels.map(channel => channel.toUpperCase()),
                reminderType,
                intervalHours,
                daysOfWeek,
                isActive,
                ...(schedule ? { sendTimes: schedule.sendTimes, timezone: schedule.timezone, ...(schedule.timezoneStrategy ? { timezoneStrategy: schedule.timezoneStrategy } : {}) } : {}),
            };

            if (recipientCriteria && Object.keys(recipientCriteria).length > 0) {
                requestData.recipientCriteria = recipientCriteria;
            } else {
                requestData.recipientUserIds = recipientUserIds;
            }

            const {
                data: { result, isValid, message },
            } = await api.put<
                DefaultRes<MessageCenterTypes.MessageReminder>,
                AxiosResponse<DefaultRes<MessageCenterTypes.MessageReminder>>
            >(`/message-reminders/${reminderId}`, requestData);

            MessageCenterService.dispatch.toggleLoading('updateMessage', false);

            MessageCenterService.warnIfScheduleNotEchoed(schedule, isValid, result);
            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;

        } catch (error: any) {
            MessageCenterService.dispatch.toggleLoading('updateMessage', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MessageCenterTypes.MessageReminder>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Delete an automated reminder
     */
    public static async deleteAutomatedReminder(
        reminderId: string,
        callback?: (isValid?: boolean, message?: string) => void,
    ): Promise<void> {
        try {
            const {
                data: { isValid, message },
            } = await api.delete<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`/message-reminders/${reminderId}`);

            callback && callback(isValid, RESPONSE_CODES[message]);

        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<any>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Toggle active status of an automated reminder
     */
    public static async toggleAutomatedReminderActive(
        reminderId: string,
        callback?: (isValid?: boolean, message?: string, result?: MessageCenterTypes.MessageReminder) => void,
    ): Promise<MessageCenterTypes.MessageReminder | void> {
        
        try {
            const {
                data: { result, isValid, message },
            } = await api.patch<
                DefaultRes<MessageCenterTypes.MessageReminder>,
                AxiosResponse<DefaultRes<MessageCenterTypes.MessageReminder>>
            >(`/message-reminders/${reminderId}/toggle-active`, {});

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;

        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MessageCenterTypes.MessageReminder>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }

    /**
     * Initialize edit mode for automated reminder
     */
    public static async initializeAutomatedReminderEditMode(
        reminderId: string,
        callback?: (isValid?: boolean, message?: string) => void,
    ): Promise<void> {
        
        try {
            // Get the reminder details
            const reminder = await MessageCenterService.getAutomatedReminderById(
                reminderId,
                (isValid, errorMessage, result) => {
                    if (!isValid || !result) {
                        callback && callback(false, errorMessage || 'Failed to load reminder');
                        return;
                    }

                    // Set edit mode in Redux
                    MessageCenterService.dispatch.setEditMode({
                        batchId: null,
                        originalBatch: null,
                        reminderId: reminderId,
                        originalReminder: result,
                    })
                    MessageCenterService.dispatch.updateSelectedUserIds(result.recipientUserIds);

                    // Populate recipientCriteria for the new V2 component
                    const defaultCriteria: RecipientCriteria = {
                        groupIds: [],
                        teamIds: [],
                        userIds: [],
                        selectAllUsers: false,
                        activeFilter: 'all',
                        tagFilters: [],
                        tagLogic: 'AND',
                    };

                    if (result.recipientCriteria) {
                        MessageCenterService.dispatch.setRecipientCriteria({
                            ...defaultCriteria,
                            ...result.recipientCriteria,
                        });
                    } else {
                        // Legacy reminder: convert flat userIds to criteria format
                        MessageCenterService.dispatch.setRecipientCriteria({
                            ...defaultCriteria,
                            userIds: result.recipientUserIds,
                        });
                    }

                    // Pre-populate automated reminder state. Legacy reminders
                    // (no stored schedule) show the fixed cycle they actually
                    // run on today — type-aware, mirroring pom-2-api's sweep
                    // gating (SC+12h fires 8pm ONLY) — so saving from the
                    // edit form upgrades them without changing behavior.
                    // Dormant legacy docs (matching neither sweep) prefill
                    // the morning default: saving one is an explicit admin
                    // action that activates it, shown on screen.
                    MessageCenterService.dispatch.updateAutomatedReminderState({
                        reminderType: result.reminderType as 'CHARGING' | 'STAY_CONNECTED' | 'UPDATE_REMINDER',
                        // Dormant legacy docs can carry an out-of-enum interval
                        // (e.g. 6) that the API's [12,24,48] rule rejects and
                        // that renders as a blank threshold Select — coerce to
                        // a valid default so the activation save the dormant
                        // banner promises can actually succeed.
                        intervalHours: [12, 24, 48].includes(result.intervalHours)
                            ? result.intervalHours
                            : 24,
                        daysOfWeek: result.daysOfWeek || [],
                        sendTimes: result.sendTimes?.length
                            ? result.sendTimes
                            : (legacySendTimesFor(result.reminderType, result.intervalHours) ?? ['08:00']),
                        timezone: result.timezone || 'America/New_York',
                        // Anything else (including legacy null) prefills as fixed.
                        timezoneStrategy: result.timezoneStrategy === 'perRecipient' ? 'perRecipient' : 'fixed',
                        title: result.title,
                        body: result.body,
                        channels: result.channels,
                    });

                    callback && callback(true, 'Edit mode initialized successfully');
                }
            );

        } catch (error: any) {
            callback && callback(false, String(error));
        }
    }

    /**
     * Get all message batches with pagination and days in the past for message center history list
     */
    public static async fetchMany(
        dto: PaginatorFactory<{}>,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreState = false,
        showUpcomingBatches = true,
        daysInThePast = 30
    ): Promise<PaginationFactory<MessageCenterTypes.MessageBatch> | void> {
        const {
            dispatch: { toggleLoading, setPagination },
        } = MessageCenterService;

        if (!ignoreState) {
            toggleLoading('fetchMany', true);
        }

        try {
            const qsfy = queryStringify({
                perPage: dto.perPage,
                page: dto.page,
                showUpcomingBatches: String(showUpcomingBatches),
                daysInThePast,
                filter: {
                    ...dto.filter,
                },
            });

            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<PaginationFactory<MessageCenterTypes.MessageBatch>>,
                AxiosResponse<DefaultRes<PaginationFactory<MessageCenterTypes.MessageBatch>>>
            >(`/messages/?${qsfy}`);

            if (!ignoreState) {
                toggleLoading('fetchMany', false);

                if (isValid) {
                    setPagination({ items, page, perPage, total });
                }
            }

            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            toggleLoading('fetchMany', false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MessageCenterTypes.MessageBatch>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Get message records by batch ID
     */
    public static async getMessageRecordsByBatchId(
        batchId: string,
        callback?: (isValid?: boolean, message?: string, result?: any[]) => void,
    ): Promise<any[] | void> {
        
        try {
            const {
                data: { result, isValid, message },
            } = await api.get<
                DefaultRes<any[]>,
                AxiosResponse<DefaultRes<any[]>>
            >(`/messages/${batchId}/records`);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;

        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<any[]>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            } else {
                callback && callback(false, String(error));
            }
        }
    }
}